import { AxiosResponse } from 'axios';
import { emperor } from '~/services';

export interface IUGCDefault {
  chat_on: boolean;
  marketplace_on: boolean;
  events_on: boolean;
  groups_on: boolean;
  reviews_on: boolean;
  video_on: boolean;

  chat_past_days: number;
  marketplace_past_days: number;
  events_past_days: number;
  reviews_past_days: number;

  events_future_days: number;

  chat_snippet?: string;
  marketplace_snippet?: string;
  events_snippet?: string;
  groups_snippet?: string;
  reviews_snippet?: string;
  videos_snippet?: string;

  js_snippet?: string;

  blurb_paragraph?: string;
  blurb_label?: string;

  blurb_link?: string;

  blurb2_label?: string;
  blurb2_link?: string;
  communityUrl?: string;

  notes_chat?: string;
  notes_marketplace?: string;
  notes_events?: string;
  notes_groups?: string;
  notes_reviews?: string;
}

export interface IUGCSetting extends IUGCDefault {
  uuid: string;
  building: string;
  shortcode: string;
  marketplaceGroupId?: string;
}

export interface IUGCVideo {
  createdAt: string;
  updatedAt: string;
  uuid: string;
  video_url: string;
  visible: boolean;
  priority: number;
  building: string;
}

export interface ICreateUGCSetting extends IUGCDefault {}

export const getUGCSetting = async (
  buildingId: string
): Promise<AxiosResponse<IUGCSetting>> => {
  return await emperor.get('/ugc', {
    params: { buildingId }
  });
};

export const putUGCSetting = async (
  ugcSettings: ICreateUGCSetting | IUGCSetting
): Promise<AxiosResponse<IUGCSetting>> => {
  return await emperor.put('/ugc', ugcSettings);
};

export const getUGCVideos = async (
  buildingId: string
): Promise<AxiosResponse<IUGCVideo[]>> => {
  return await emperor.get(`/ugc/videos/${buildingId}`);
};

export const toggleUGCVideoVisibility = async (
  uuid: string
): Promise<AxiosResponse<IUGCVideo>> => {
  return await emperor.post('/ugc/video/visibility', { uuid });
};

export const updateUGCVideoPriorities = async (
  building: string,
  priorities: Record<string, number>
): Promise<AxiosResponse<IUGCVideo[]>> => {
  return await emperor.post('/ugc/video/priorities', {
    building,
    priorities
  });
};

export const createUGCVideo = async (
  building: string,
  video_url: string
): Promise<AxiosResponse<IUGCVideo>> => {
  return await emperor.post('/ugc/video', {
    building,
    video_url
  });
};

export const patchGroupComment = async (
  buildingId: string,
  commentId: string,
  isPublicFacing: boolean
): Promise<AxiosResponse<IUGCSetting>> => {
  return await emperor.post('/ugc/visible-content', {
    buildingId,
    commentId,
    isPublicFacing
  });
};

export const patchCommentImageVisibility = async (
  buildingId: string,
  imageId: string,
  isPublicFacing: boolean
): Promise<AxiosResponse<IUGCSetting>> => {
  return await emperor.post('/ugc/visible-content', {
    buildingId,
    imageId,
    isPublicFacing
  });
};

export interface ICommentImage {
  uuid: string;
  image: string;
  isPublicFacing?: boolean;
}

export interface ICommentReactions {
  reactionsCount: number;
  myReaction: string | null;
  reactionsSorted: string[];
  byReactionUserIds: Record<string, string[]>;
}

export interface IAICommentUser {
  name?: string;
  firstName?: string;
  lastName?: string;
}

export interface IAIComment {
  uuid: string;
  contents: string;
  createdAt: string;
  type: 'chat' | 'marketplace' | 'review';
  tier?: 'tier_1' | 'tier_2';
  score?: number;
  label?: string;
  name: string;
  user?: IAICommentUser;
  reviewrating?: number;
  images?: ICommentImage[];
  reactions?: ICommentReactions;
  isSelected?: boolean;
  children?: IAIComment[];
}

export interface IDashboardItem {
  uuid: string;
  contents: string;
  createdAt: string;
  authorName: string;
  type: 'chat' | 'marketplace' | 'review';
  reviewrating?: number;
  isPublicFacing?: boolean;
  images?: ICommentImage[];
  reactions?: ICommentReactions;
  isSelected?: boolean;
  children?: IDashboardItem[];
}

export interface IDashboardVisibleContent {
  chat: IDashboardItem[];
  marketplace: IDashboardItem[];
  reviews: IDashboardItem[];
}

export const getDashboardVisibleContent = async (
  buildingId: string,
  type?: string
): Promise<AxiosResponse<any>> => {
  return await emperor.get(`/ugc/dashboard-visible-content/${buildingId}`, {
    params: type ? { type } : undefined
  });
};

/** Why a section has no AI picks. See run-pipeline.js resolveStatus. */
export type AISectionStatus =
  | 'ok'
  | 'disabled'
  | 'no_content'
  | 'all_filtered'
  | 'llm_error'
  | 'failed'
  | 'no_matches'
  | 'below_threshold';

export interface IAICommentsResponse {
  /** Number of pre-selected posts per section. */
  counts: {
    chat: number;
    marketplace: number;
    reviews: number;
  };
  status?: {
    chat: AISectionStatus;
    marketplace: AISectionStatus;
    reviews: AISectionStatus;
  };
  chat: IAIComment[];
  marketplace: IAIComment[];
  reviews: IAIComment[];
}

export const getAIComments = async (
  buildingId: string
): Promise<AxiosResponse<IAICommentsResponse>> => {
  return await emperor.get(`/ugc/${buildingId}/ai-content`);
};

export interface IMarkVisibleContentResponse {
  message: string;
  commentsUpdated: number;
  marketplaceUpdated: number;
  reviewsUpdated: number;
}

export const markVisibleContent = async (
  buildingId: string,
  comments: string[],
  marketplace: string[],
  reviews: string[]
): Promise<AxiosResponse<IMarkVisibleContentResponse>> => {
  return await emperor.post('/ugc/mark-visible-content', {
    buildingId,
    comments,
    marketplace,
    reviews
  });
};

export interface IUnpublishAllResponse {
  message: string;
  commentsUpdated: number;
  marketplaceUpdated: number;
}

export const unpublishAllVisibleContent = async (
  buildingId: string,
  section: 'chat' | 'marketplace'
): Promise<AxiosResponse<IUnpublishAllResponse>> => {
  return await emperor.post(
    `/ugc/unpublish-all-visible-content/${buildingId}`,
    { section }
  );
};

export const patchReviewVisibility = async (
  buildingId: string,
  reviewId: string,
  isPublicFacing: boolean
): Promise<AxiosResponse<IUGCSetting>> => {
  return await emperor.post('/ugc/visible-content', {
    buildingId,
    section: 'reviews',
    reviewId,
    isPublicFacing
  });
};

export const patchEventVisibility = async (
  buildingId: string,
  eventId: string,
  isPublicFacing: boolean
): Promise<AxiosResponse<IUGCSetting>> => {
  return await emperor.post('/ugc/visible-content', {
    buildingId,
    section: 'events',
    eventId,
    isPublicFacing
  });
};

export const patchGroupVisibility = async (
  buildingId: string,
  groupId: string,
  isPublicFacing: boolean
): Promise<AxiosResponse<IUGCSetting>> => {
  return await emperor.post('/ugc/visible-content', {
    buildingId,
    section: 'groups',
    groupId,
    isPublicFacing
  });
};

export const approveAIContent = async (
  buildingId: string,
  aiData: IAICommentsResponse,
  selectedUuids: Set<string>
): Promise<AxiosResponse<IMarkVisibleContentResponse>> => {
  const chatUuids = new Set<string>();
  const marketplaceUuids = new Set<string>();
  const reviewUuids = new Set<string>();

  aiData.chat.forEach((item) => {
    chatUuids.add(item.uuid);
    item.children?.forEach((c) => chatUuids.add(c.uuid));
  });
  aiData.marketplace.forEach((item) => {
    marketplaceUuids.add(item.uuid);
    item.children?.forEach((c) => marketplaceUuids.add(c.uuid));
  });
  aiData.reviews.forEach((item) => reviewUuids.add(item.uuid));

  return markVisibleContent(
    buildingId,
    Array.from(selectedUuids).filter((id) => chatUuids.has(id)),
    Array.from(selectedUuids).filter((id) => marketplaceUuids.has(id)),
    Array.from(selectedUuids).filter((id) => reviewUuids.has(id))
  );
};
